import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantBrandProvider, useTenantBrand } from "@/context/TenantBrandContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import TenantEntry from "@/pages/TenantEntry";
import Register from "@/pages/Register";
import Portal from "@/pages/Portal";
import SuperAdmin from "@/pages/SuperAdmin";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Teachers from "@/pages/Teachers";
import Classes from "@/pages/Classes";
import Schedule from "@/pages/Schedule";
import Attendance from "@/pages/Attendance";
import Journal from "@/pages/Journal";
import Settings from "@/pages/Settings";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import Receipt from "@/pages/Receipt";
import { Loader2 } from "lucide-react";

const TENANT_ROLES = ["administrator", "keuangan", "customer_service"];

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "super_admin" ? "/superadmin" : "/dashboard"} replace />;
  }
  return children;
}

function HomeByRole() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/superadmin" replace />;
  if (user?.role === "customer_service") return <Navigate to="/siswa" replace />;
  return <Dashboard />;
}

function PublicHome() {
  const { brand, loading } = useTenantBrand();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#040D1D]" data-testid="brand-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#00D1A8]" />
      </div>
    );
  }
  return brand ? <Navigate to="/login" replace /> : <Landing />;
}

function TenantOnlyGuard({ children }) {
  const { brand } = useTenantBrand();
  return brand ? <Navigate to="/login" replace /> : children;
}

function App() {
  return (
    <BrowserRouter>
      <TenantBrandProvider>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<PublicHome />} />
          <Route path="/login" element={<Login />} />
          <Route path="/s/:slug" element={<TenantEntry />} />
          <Route path="/daftar" element={<TenantOnlyGuard><Register /></TenantOnlyGuard>} />
          <Route path="/portal/:studentId" element={<Portal />} />
          <Route path="/kwitansi/:id" element={<Protected><Receipt /></Protected>} />
          <Route path="/superadmin" element={<Protected roles={["super_admin"]}><SuperAdmin /></Protected>} />
          <Route element={<Protected roles={TENANT_ROLES}><Layout /></Protected>}>
            <Route path="/dashboard" element={<HomeByRole />} />
            <Route path="/siswa" element={<Students />} />
            <Route path="/guru" element={<Teachers />} />
            <Route path="/kelas" element={<Classes />} />
            <Route path="/jadwal" element={<Schedule />} />
            <Route path="/absensi" element={<Attendance />} />
            <Route path="/pengaturan" element={<Settings />} />
            <Route path="/spp" element={<Payments />} />
            <Route path="/pengeluaran" element={<Expenses />} />
            <Route path="/jurnal" element={<Journal />} />
            <Route path="/laporan" element={<Reports />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </TenantBrandProvider>
    </BrowserRouter>
  );
}

export default App;
